import * as dfns from 'date-fns';
import enLoc from 'date-fns/locale/en-US';
import csLoc from 'date-fns/locale/cs';
import { t } from '../locales/i18n';

const EMPTY_STRING = '';

export type DateLocaleCode = 'cs' | 'en-US';

export type FormatLength = 'tiny' | 'short' | 'medium' | 'long';

export type DateTimeFormat = {
  [width in FormatLength]: string;
};

export type NiQLocale = {
  locale: Locale;
  dateFormat: DateTimeFormat;
  timeFormat: DateTimeFormat;
};

export type DateLocales = {
  [key in DateLocaleCode]: NiQLocale;
};

export type DateLocaleListener = (code: string, locale: NiQLocale) => void;

const listeners: DateLocaleListener[] = [];

/**
 * Register callback function to be called in case locale will be changed.
 * @param fn callback function
 */
export function addLocaleListener(fn: DateLocaleListener): void {
  listeners.push(fn);
}

export const dateLocales: DateLocales = {
  cs: {
    locale: csLoc,
    dateFormat: {
      tiny: 'dd.MM.', // 14.04.
      short: 'dd.MM.yyyy', // 14.04.2020
      medium: 'eee d.M.', // pondeli 14.4.
      long: 'eee d.M.y' // pondeli 14.4.2020
    },
    timeFormat: {
      tiny: 'H:mm',
      short: 'HH:mm',
      medium: 'HH:mm:ss',
      long: 'HH:mm:ss'
    }
  },
  'en-US': {
    locale: enLoc,
    dateFormat: {
      tiny: 'MM/dd', // 04/14
      short: 'MM/dd/yyyy', // 04/14/2020
      medium: 'eee M/d', // Monday 4/14
      long: 'eee M/d/y' // Monday 4/14/2020
    },
    timeFormat: {
      tiny: 'H:mm',
      short: 'HH:mm',
      medium: 'HH:mm:ss',
      long: 'HH:mm:ss'
    }
  }
};

let dateLocale = dateLocales.cs;

export function setupDateLocale(loc: DateLocaleCode) {
  dateLocale = dateLocales[loc] || dateLocales['cs'];
  const localeCode = dateLocale.locale.code || 'cs';
  listeners.map((fn) => fn(localeCode, dateLocale)); // call listeners
}

export function getDateLocale(): NiQLocale {
  return dateLocale;
}

/**
 * Get locale specific formating string for date.
 * tiny = day & month
 * short = day, month, year
 * medium = day, month name, full year
 * long = day of week, day, month name, full year
 * Note: actual implementation depends on specific locale see date-fns source code at
 * https://github.com/date-fns/date-fns/tree/master/src/locale/{your locale}/_lib/formatLong/index.js
 * @param s format type
 */
export function getLocaleDateString(s: FormatLength = 'short'): string {
  return dateLocale.dateFormat[s];
}

export function getLocaleTimeString(s: FormatLength = 'short'): string {
  return dateLocale.timeFormat[s];
}

function isSameDay(dt1: Date, dt2: Date): boolean {
  return dt1 && dt2 ? dfns.isSameDay(dt1, dt2) : false;
}

export function today(): Date {
  return dfns.startOfDay(new Date());
}

export function tomorrow(): Date {
  return dfns.addDays(today(), 1);
}

export function forever(): Date {
  return new Date(2200, 0, 1);
}

export function never(): Date {
  return new Date(1900, 0, 1);
}

export function getDateFromValidityKey(
  validity?: 'tomorrow' | 'forever' | 'never'
): Date {
  switch (validity) {
    case 'tomorrow': {
      return tomorrow();
    }
    case 'forever': {
      return forever();
    }
    case 'never': {
      return never();
    }
    default: {
      return today();
    }
  }
}

export function isToday(dt: Date | null): boolean {
  return dt ? isSameDay(dt, new Date()) : false;
}

export function isTomorrow(dt: Date | null): boolean {
  return dt ? isSameDay(dt, tomorrow()) : false;
}

export function isForever(dt: Date): boolean {
  return (
    !dfns.isDate(dt) || dfns.isAfter(dt, dfns.subMilliseconds(forever(), 1))
  );
}

export function isNever(dt: Date): boolean {
  return (
    !dfns.isDate(dt) || dfns.isBefore(dt, dfns.addMilliseconds(never(), 1))
  );
}

export function fromNow(dt: Date | null): string {
  return dt
    ? dfns.formatDistanceToNow(dt, {
        addSuffix: true,
        locale: dateLocale.locale
      })
    : EMPTY_STRING;
}

/**
 * Return ISO date representation of specified date
 * @param dt date
 */
export function isoDate(dt: Date | null): string {
  return dt ? dfns.format(dt, getLocaleDateString()) : EMPTY_STRING;
}

/**
 * Format datetime according to provided format string using currently set locale
 * @param dt datetime to format
 * @param fmt format string (see date-fns format syntax)
 */
export function formatDateTime(dt: Date | null, fmt: string): string {
  return dt ? dfns.format(dt, fmt, { locale: dateLocale.locale }) : '';
}

/**
 * Get localized short date - day and month only
 * @param dt Date
 */
export function tinyDate(dt: Date | null): string {
  return formatDateTime(dt, dateLocale.dateFormat['tiny']);
}

/**
 * Get localized full/short date including day of week
 * e.g. "Mon 12.5.2019" or "Mon 5/12/2019"
 * @param dt date to be formatted
 * @param opts if prop withYear is true, full date is returned
 */
export function shortWeekDate(
  dt: Date | null,
  opts = { withYear: false }
): string {
  return formatDateTime(
    dt,
    opts.withYear
      ? dateLocale.dateFormat['long']
      : dateLocale.dateFormat['medium']
  );
}

export function naturalDateRange(dt1?: Date | null, dt2?: Date | null): string {
  if (!dt1 || !dt2) return EMPTY_STRING + '-' + EMPTY_STRING;
  if (dfns.isSameDay(dt1, dt2)) {
    return shortWeekDate(dt1, { withYear: true });
  }
  if (dfns.isSameYear(dt1, dt2)) {
    return `${shortWeekDate(dt1)} - ${shortWeekDate(dt2, { withYear: true })}`;
  }
  return `${shortWeekDate(dt1, { withYear: true })} - ${shortWeekDate(dt2, {
    withYear: true
  })}`;
}

export function shortTime(dt: Date | null): string {
  const fmt = getLocaleTimeString();
  return formatDateTime(dt, fmt);
}

export function tinyDateTime(dt: Date): string {
  return `${tinyDate(dt)} ${shortTime(dt)}`;
}

export function naturalShortTime(dt: Date | null): string {
  if (!dt) return EMPTY_STRING;
  if (isToday(dt)) {
    return shortTime(dt);
  } else {
    return tinyDateTime(dt);
  }
}

export function mapValidUntil(dt: Date) {
  if (isForever(dt)) {
    return { key: 'forever', title: t('validUntil.forever') };
  }
  if (isToday(dt)) {
    return { key: 'today', title: t('validUntil.today') };
  }
  if (isTomorrow(dt)) {
    return { key: 'tomorrow', title: t('validUntil.tomorrow') };
  }
  if (dfns.isBefore(dt, today())) {
    return { key: 'never', title: t('validUntil.never') };
  }
  return {
    key: 'custom',
    title: t('validUntil.custom', { validUntil: tinyDate(dt) })
  };
}

export function timeIsValid(timeStr: string | null): boolean {
  const allowedFormats = ['HH:mm', 'H:m', 'H:', 'H'];
  const d = new Date();
  return allowedFormats.some((fmt) =>
    dfns.isValid(
      dfns.parse(timeStr || '', fmt, d, { locale: dateLocale.locale })
    )
  );
}

/**
 * Check if provided string is valid ISO date
 * @param dateStr string containg ISO date
 */
export function dateIsValid(dateStr: string | null = ''): boolean {
  return dfns.isValid(dfns.parseISO(dateStr || ''));
}

export function isTodayOrFutureDay(dateStr: string): boolean {
  const d = new Date();
  const fmt = getLocaleDateString();
  return dfns.isAfter(
    dfns.subMilliseconds(
      dfns.parse(dateStr, fmt, d, { locale: dateLocale.locale }),
      1
    ),
    today()
  );
}

export function time2str(date: Date | number): string {
  if (dfns.isValid(date)) {
    const fmt = getLocaleTimeString();
    return dfns.format(date, fmt, { locale: dateLocale.locale });
  }
  return '';
}

export function str2time(timeStr: string): Date | null {
  const fmt = getLocaleTimeString();
  const dt = dfns.parse(timeStr, fmt, new Date(), {
    locale: dateLocale.locale
  });
  return dfns.isValid(dt) ? dt : null;
}

export function str2dt(dateStr?: string | null): Date | null {
  const d = new Date(2000, 0, 1);
  const fmt = getLocaleDateString();
  const dt = dfns.parse(dateStr || 'a', fmt, d, { locale: dateLocale.locale });
  return dfns.isValid(dt) ? dt : null;
}

export function parseISO(dateStr?: string | null): Date | null {
  const dt = dfns.parseISO(dateStr || '');
  return dfns.isValid(dt) ? dt : null;
}
